import { useMemo, useState } from 'react';
import { Search } from '../../../icons';
import type { OrgDivision, OrgTeam } from '../../../api/admin-org';

/**
 * 조직도 카드 (2026-09-13 · H) — 시안 axi-employees-hr-list 의 왼쪽 열.
 *
 * 회사 › 본부 › 팀 · 본부 없는 팀은 「회사 직속 팀」 묶음. 누르면 목록이 그 조직으로 걸러진다.
 * ★ 본부 · 팀을 **만드는 곳은 관리 콘솔의 조직 관리**다 — 「+ 본부 · + 팀」은 자리만 두고 잠근다
 *   (길을 둘 내면 «누가 바꿨나» 가 갈린다 — 계정 칸과 같은 규칙).
 */
export type OrgSelection =
  | { kind: 'all' }
  | { kind: 'division'; id: string; name: string }
  | { kind: 'direct' }
  | { kind: 'team'; id: string; name: string; divisionName: string | null };

export interface EmployeeOrgTreeProps {
  divisions: readonly OrgDivision[] | null;
  teams: readonly OrgTeam[] | null;
  total: number;
  selection: OrgSelection;
  onSelect: (next: OrgSelection) => void;
  companyName?: string;
}

const sameSel = (a: OrgSelection, b: OrgSelection) =>
  a.kind === b.kind && ('id' in a ? a.id : '') === ('id' in b ? b.id : '');

export function EmployeeOrgTree({ divisions, teams, total, selection, onSelect, companyName = '전체' }: EmployeeOrgTreeProps) {
  const [q, setQ] = useState('');
  const [closed, setClosed] = useState<Record<string, boolean>>({});
  const needle = q.trim();

  const groups = useMemo(() => {
    if (!divisions || !teams) return null;
    const match = (s: string) => !needle || s.includes(needle);
    const sum = (ts: readonly OrgTeam[]) => ts.reduce((n, t) => n + (t.memberCount ?? 0), 0);
    const divs = divisions.map((d) => {
      const ts = teams.filter((t) => t.divisionId === d.id);
      const shown = match(d.name) ? ts : ts.filter((t) => match(t.name));
      return { d, teams: shown, count: sum(ts), visible: match(d.name) || shown.length > 0 };
    }).filter((g) => g.visible);
    const directAll = teams.filter((t) => !t.divisionId);
    const direct = directAll.filter((t) => match(t.name) || match('회사 직속 팀'));
    return { divs, direct, directCount: sum(directAll) };
  }, [divisions, teams, needle]);

  const node = (sel: OrgSelection, cls: string, label: string, count: number | null, extra?: JSX.Element) => (
    <button type="button" className={`hre-node ${cls}`} aria-pressed={sameSel(selection, sel)} onClick={() => onSelect(sel)}>
      {extra}<span className="nm">{label}</span>{count !== null && <em className="num">{count}</em>}
    </button>
  );

  const toggle = (key: string, label: string) => (
    <button
      type="button" className="hre-car" aria-expanded={!closed[key]} aria-label={`${label} ${closed[key] ? '펼치기' : '접기'}`}
      onClick={() => setClosed((c) => ({ ...c, [key]: !c[key] }))}
    />
  );

  return (
    <aside className="hre-card hre-org" aria-label="조직도">
      <div className="hre-cb">
        <div className="hre-ch"><h2>조직도</h2><span className="hre-sub">회사 · 본부 · 팀</span></div>
        <label className="hre-mini">
          <span className="hre-sr">조직 검색</span>
          <Search aria-hidden />
          <input type="search" placeholder="본부 · 팀 이름" value={q} onChange={(e) => setQ(e.target.value)} />
        </label>
        {!groups ? (
          <p className="hre-path">조직 목록을 읽지 못했습니다 — 목록은 전체로 봅니다.</p>
        ) : (
          <ul className="hre-tree">
            <li>
              {node({ kind: 'all' }, 'hre-node--co', companyName, total, <span className="mark" aria-hidden>{companyName.slice(0, 2)}</span>)}
              <ul className="hre-lvl">
                {groups.divs.map(({ d, teams: ts, count }) => (
                  <li key={d.id}>
                    <div className="hre-brow">
                      {toggle(d.id, d.name)}
                      {node({ kind: 'division', id: d.id, name: d.name }, 'hre-node--dept', d.name, count)}
                    </div>
                    {!closed[d.id] && ts.length > 0 && (
                      <ul className="hre-kids">
                        {ts.map((t) => <li key={t.id}>{node({ kind: 'team', id: t.id, name: t.name, divisionName: d.name }, 'hre-node--team', t.name, t.memberCount ?? 0)}</li>)}
                      </ul>
                    )}
                  </li>
                ))}
                {groups.direct.length > 0 && (
                  <li className="hre-direct">
                    <div className="hre-brow">
                      {toggle('__direct', '회사 직속 팀')}
                      {node({ kind: 'direct' }, 'hre-node--grp', '회사 직속 팀', groups.directCount)}
                    </div>
                    {!closed.__direct && (
                      <ul className="hre-kids">
                        {groups.direct.map((t) => <li key={t.id}>{node({ kind: 'team', id: t.id, name: t.name, divisionName: null }, 'hre-node--team', t.name, t.memberCount ?? 0)}</li>)}
                      </ul>
                    )}
                  </li>
                )}
              </ul>
            </li>
          </ul>
        )}
        <div className="hre-orgfoot">
          <button type="button" className="hre-btn hre-btn--line hre-btn--sm" disabled title="본부는 관리 콘솔의 조직 관리에서 추가합니다">+ 본부</button>
          <button type="button" className="hre-btn hre-btn--line hre-btn--sm" disabled title="팀은 관리 콘솔의 조직 관리에서 추가합니다">+ 팀</button>
        </div>
      </div>
    </aside>
  );
}

/** 선택한 조직에 속하는 행인가 — 목록 거르기. */
export function inSelection(teamId: string | null, sel: OrgSelection, teams: readonly OrgTeam[] | null): boolean {
  if (sel.kind === 'all') return true;
  if (sel.kind === 'team') return teamId === sel.id;
  if (!teams || !teamId) return false;
  const team = teams.find((t) => t.id === teamId);
  if (!team) return false;
  return sel.kind === 'division' ? team.divisionId === sel.id : !team.divisionId;
}

export function selectionPath(sel: OrgSelection, companyName = '전체'): string {
  if (sel.kind === 'all') return `${companyName} 직원`;
  if (sel.kind === 'division') return `${sel.name} 전체`;
  if (sel.kind === 'direct') return '부서 없이 회사 직속인 팀 전체';
  return sel.divisionName ? `${sel.divisionName} › ${sel.name}` : `회사 직속 › ${sel.name}`;
}
